import type { JSX, ReactNode } from "react";
import { Suspense } from "react";
import { Canvas } from "@react-three/fiber";
import {
  AdaptiveDpr,
  AdaptiveEvents,
  OrbitControls,
  Preload,
} from "@react-three/drei";
import { useSceneStore } from "@/state/useSceneStore";
import { ErrorBoundary } from "@/ui/ErrorBoundary";
import { Poster } from "@/ui/Loader/Poster";
import { Room } from "@/scene/Room";
import { RoomLights } from "@/scene/lights/RoomLights";
import { FanLights } from "@/scene/lights/FanLights";
import { WallLights } from "@/scene/lights/WallLights";
import { Book } from "@/scene/objects/Book";
import { Monitor } from "@/scene/objects/Monitor";
import { MonitorScreen } from "@/scene/objects/MonitorScreen";
import { PinBoard } from "@/scene/objects/PinBoard";
import { LightSwitch } from "@/scene/objects/LightSwitch";
import { Cube } from "@/scene/objects/Cube";
import { Keyboard } from "@/scene/objects/Keyboard";
import { Plant } from "@/scene/objects/Plant";
import { Phone } from "@/scene/objects/Phone";
import { Cpu } from "@/scene/objects/Cpu";
import { Mug } from "@/scene/objects/Mug";
import { WallTitle } from "@/scene/objects/WallTitle";
import { DustField } from "@/scene/fx/DustField";
import { Postprocessing } from "@/scene/fx/Postprocessing";
import { Hotspots } from "@/scene/fx/Hotspots";
import { CalibrationGui } from "@/scene/cameras/CalibrationGui";
import { CameraDriver } from "@/scene/cameras/CameraDriver";

interface SceneProps {
  children?: ReactNode;
}

const dprFor = (q: "low" | "med" | "high"): number =>
  q === "low" ? 1 : q === "med" ? 1.25 : 1.5;

const shadowsFor = (q: "low" | "med" | "high"): boolean => q !== "low";

export const Scene = ({ children }: SceneProps): JSX.Element => {
  const quality = useSceneStore((s) => s.quality);
  const activeSection = useSceneStore((s) => s.activeSection);
  const focusedObject = useSceneStore((s) => s.focusedObject);
  const controlsEnabled = activeSection === null && focusedObject === null;
  return (
    <ErrorBoundary fallback={<Poster />}>
      <Canvas
        camera={{
          fov: 75,
          near: 0.01,
          far: 1000,
          position: [1.009, 0.546, 0.498],
        }}
        dpr={[1, dprFor(quality)]}
        shadows={shadowsFor(quality)}
        gl={{ antialias: true }}
      >
        <Suspense fallback={null}>
          <Preload all />
          <AdaptiveDpr pixelated />
          <AdaptiveEvents />
          <RoomLights />
          <FanLights />
          <WallLights />
          <Room />
          <MonitorScreen />
          <DustField />
          <Book />
          <Monitor />
          <PinBoard />
          <LightSwitch />
          <Cube />
          <Keyboard />
          <Plant />
          <Phone />
          <Cpu />
          <Mug />
          <WallTitle />
          <Postprocessing />
          <Hotspots />
          <CameraDriver />
          <OrbitControls
            makeDefault
            enabled={controlsEnabled}
            target={[0.2, 0.11, 0.1]}
            enablePan={false}
            minDistance={0.9}
            maxDistance={1.35}
            minAzimuthAngle={0.2}
            maxAzimuthAngle={Math.PI * 0.78}
            minPolarAngle={0.3}
            maxPolarAngle={Math.PI / 2}
          />
          {import.meta.env.DEV && <CalibrationGui />}
          {children}
        </Suspense>
      </Canvas>
    </ErrorBoundary>
  );
};
